import { Component, Inject, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { B3SelectItem } from 'src/app/shared/models/b3-select-item.model';
import { Subscription, catchError, map, merge, Observable, startWith } from 'rxjs';
import { AddressUtil } from 'src/app/helpers/utils/address.util';
import { BiomeUtil } from 'src/app/helpers/utils/biome.util';
import { AddressService } from 'src/app/services/address/address.service';
import { CitiesResponse, ParticipantCities } from 'src/app/shared/interfaces/address';
import { PsaProjectData } from 'src/app/shared/interfaces/psa-project-register';
import { RegisterFormSharedUtil } from 'src/app/helpers/utils/register-form-shared.util';
import { formatCpfCnpjInputValue, getDocumentCharacterCount, resolveCpfCnpjInputMask } from 'src/app/helpers/utils/cpf-cnpj.util';
import { FORM_REGISTER } from 'src/app/pages/projects-repository/register/project/project-register.component';
import { AddOthersOwnersComponent } from '../../psa-add-others-owners/psa-add-others-owners.component';

@Component({
  standalone: false,
  selector: 'app-psa-step-property',
  templateUrl: './step-property.component.html',
  styleUrls: ['./step-property.component.scss']
})
export class PsaStepPropertyComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('ownersList') ownersList?: AddOthersOwnersComponent;

  includePropertyDisabled = true;
  private includeDisabledSubscription?: Subscription;

  public today = new Date();
  public maxDate: Date;
  public minDate: Date;
  public errorMsg = 'Data inválida ou não preenchida';
  public biomeUtil: BiomeUtil = new BiomeUtil();
  public addressUtil: AddressUtil = new AddressUtil();
  characterCount: number = 0;
  date: boolean = true;
  isInvalidDate: boolean = false;
  selectBiome: B3SelectItem[] = [...this.biomeUtil.biomes];
  selectUF: B3SelectItem[] = [...this.addressUtil.estadosBrasil];
  selectCity: B3SelectItem[] = [];
  cityInput: string = '';

  get ownerDocumentMask(): 'text' | 'cnpj' {
    return resolveCpfCnpjInputMask(this.formRegister.get('landOwnerCpf')?.value);
  }

  constructor(
    @Inject(FORM_REGISTER) public formRegister: FormGroup,
    @Inject('projectDraftData') public projectDraftData: PsaProjectData,
    private addressService: AddressService
  ) {
    const currentYear = new Date().getFullYear();
    this.maxDate = new Date(currentYear, 11, 31);
    this.minDate = new Date(1992, 0, 1);
  }

  ngOnInit(): void {
    this.formRegister.get('country')?.setValue('Brasil');
    this.normalizeTextControlValue('onlineStatementLink');
    this.normalizeTextControlValue('registrationRecord');
    this.sanitizeOnlineStatementLink();

    this.formRegister.get('landOwnerFullName')?.valueChanges
      .pipe(startWith(this.formRegister.get('landOwnerFullName')?.value || ''))
      .subscribe(value => {
        const text = value == null ? '' : String(value);
        if (text.length > 100) {
          const truncated = text.slice(0, 100);
          this.formRegister.get('landOwnerFullName')?.setValue(truncated);
          this.characterCount = 100;
          return;
        }
        this.characterCount = text.length;
      });

    this.formRegister.get('city')?.valueChanges.subscribe(() => {
      this.sanitizeOnlineStatementLink();
    });

    this.formRegister.get('onlineStatementLink')?.valueChanges.subscribe(() => {
      this.sanitizeOnlineStatementLink();
    });
    this.formRegister.get('totalHectares')?.valueChanges.subscribe(value => {
      const total = Number(value) || 0;
      if (total > 0) {
        this.formRegister.get('carbonGenerationHectares')?.enable();
      } else {
        this.formRegister.get('carbonGenerationHectares')?.disable();
        this.formRegister.get('carbonGenerationHectares')?.setValue('');
      }
      RegisterFormSharedUtil.calculateAvailableHectares(this.formRegister);
    });
    this.formRegister.get('carbonGenerationHectares')?.disable();
    this.formRegister.get('carbonGenerationHectares')?.valueChanges.subscribe(() => {
      RegisterFormSharedUtil.calculateAvailableHectares(this.formRegister);
    });
  }

  ngAfterViewInit(): void {
    this.syncIncludePropertyDisabled();
    setTimeout(() => this.sanitizeOnlineStatementLink());

    const triggerSync = () => this.syncIncludePropertyDisabled();
    this.includeDisabledSubscription = merge(
      this.formRegister.valueChanges,
      this.formRegister.get('carbonGenerationHectares')!.valueChanges,
      this.formRegister.get('uf')!.valueChanges,
      this.formRegister.get('city')!.valueChanges,
    ).subscribe(() => triggerSync());
  }

  ngOnDestroy(): void {
    this.includeDisabledSubscription?.unsubscribe();
  }

  syncIncludePropertyDisabled(): void {
    this.includePropertyDisabled = this.ownersList?.validateDisabledButton() ?? true;
  }

  onIncludeProperty(): void {
    const added = this.ownersList?.addLocations() ?? false;
    if (added) {
      this.formRegister.get('locations')?.updateValueAndValidity();
    }
    this.syncIncludePropertyDisabled();
  }

  loadCitiesByStateAbbreviation(stateAbbreviation: string): Observable<ParticipantCities[] | undefined> {
    return this.addressService.getCitiesByAbbreviation(stateAbbreviation).pipe(
      map((res: CitiesResponse) => res?.data?.participantCities || undefined),
      catchError(() => [undefined])
    );
  }

  onBlurDate(date: Date, inputId: string) {
    const r = RegisterFormSharedUtil.onBlurDate(this.formRegister, date, inputId, this.today, this.minDate);
    this.date = r.dateFlag;
    this.isInvalidDate = r.isInvalid;
  }

  checkHasError(fieldName: string, errorCode?: string): boolean {
    const field = this.formRegister.get(fieldName);
    if (!field || !field.touched) {
      return false;
    }

    if (errorCode == null) {
      return field.invalid;
    }

    if (errorCode === 'digit') {
      return field.hasError('digit') || field.hasError('invalidChecksum');
    }

    return field.hasError(errorCode);
  }

  onChangeState(selectedState: B3SelectItem) {
    if (selectedState) {
      this.selectCity = [];
      this.loadCitiesByStateAbbreviation(selectedState.inputId as string).subscribe(cities => {
        if (cities) {
          const itemsSelect: B3SelectItem[] = RegisterFormSharedUtil.mapCitiesToSelect(cities);
          this.selectCity = itemsSelect;
          this.formRegister.get('city')?.setValue('');
        }
      });
    }
  }

  filterCities() {
    if (this.cityInput) {
      this.selectCity = RegisterFormSharedUtil.filterCities(this.selectCity, this.cityInput);
    }
  }

  onChangesInputCpfOrCnpj(): void {
    const control = this.formRegister.get('landOwnerCpf');
    const next = formatCpfCnpjInputValue(control?.value, true);
    if (control?.value !== next) {
      control?.setValue(next, { emitEvent: false });
    }
  }

  calculateAvailableHectares() {
    RegisterFormSharedUtil.calculateAvailableHectares(this.formRegister);
  }

  isLocationsMissing(): boolean {
    const control = this.formRegister.get('locations');
    return !!control?.hasError('required') && !!control.touched;
  }

  private normalizeTextControlValue(controlName: string): void {
    const control = this.formRegister.get(controlName);
    const value = control?.value;

    if (value != null && typeof value === 'object') {
      control?.setValue('', { emitEvent: false });
    }
  }

  private getCityLabel(): string {
    const city = this.formRegister.get('city')?.value;
    if (city == null) {
      return '';
    }

    if (typeof city === 'string') {
      return city.trim();
    }

    return String(city.label ?? city.data ?? '').trim();
  }

  private sanitizeOnlineStatementLink(): void {
    const control = this.formRegister.get('onlineStatementLink');
    if (!control) {
      return;
    }

    const value = String(control.value ?? '').trim();
    if (!value) {
      return;
    }

    const cityLabel = this.getCityLabel();
    if (cityLabel && value === cityLabel) {
      control.setValue('', { emitEvent: false });
    }
  }
}